/**
 * 알림톡 - 전화번호로 회원 조회 후 멤버십 가입 알림톡 재발송
 * 미등록 회원은 멤버십 가입 후 자동 알림톡 발송
 * 화면 숫자패드로 전화번호 입력 (안드로이드 키보드 미사용)
 */

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getMemberByPhone, resendQrAlimtalk, registerMemberOnly } from '../services/api';
import StoreBadge from '../components/StoreBadge';
import { isBridgeApp } from '../utils/isBridgeApp';
import './AlimtalkPage.css';

const NUMPAD_KEYS = [
  ['1', '2', '3'],
  ['4', '5', '6'],
  ['7', '8', '9'],
  ['clear', '0', 'backspace'],
];

const AlimtalkPage = () => {
  const navigate = useNavigate();

  const [digits, setDigits] = useState('');
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [member, setMember] = useState(null);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  // 미등록 회원 가입 플로우
  const [showNewMemberConfirm, setShowNewMemberConfirm] = useState(false);
  const [autoRegistering, setAutoRegistering] = useState(false);

  // 숫자 → 포맷 (010-XXXX-XXXX) - digits는 뒤 8자리만 저장
  const formatPhone = (d) => {
    if (d.length === 0) return '';
    if (d.length <= 4) return `010-${d}`;
    return `010-${d.slice(0, 4)}-${d.slice(4)}`;
  };

  const displayPhone = digits.length > 0 ? formatPhone(digits) : '';

  // 숫자패드 입력
  const handleKeyPress = (key) => {
    setError(null);
    if (key === 'backspace') {
      setDigits((prev) => prev.slice(0, -1));
    } else if (key === 'clear') {
      setDigits('');
    } else {
      setDigits((prev) => (prev.length < 8 ? prev + key : prev));
    }
  };

  // 회원 조회
  const handleSearch = async () => {
    if (digits.length !== 8) {
      setError('전화번호 뒤 8자리를 입력해주세요');
      return;
    }

    const fullPhone = '010' + digits;

    setLoading(true);
    setError(null);
    setMember(null);
    setResult(null);

    try {
      const res = await getMemberByPhone(fullPhone);
      setMember(res.member);
    } catch (err) {
      if (err.status === 404 || err.error_code === 'MEMBER_NOT_FOUND') {
        // 미등록 회원 → 가입 확인 팝업
        setShowNewMemberConfirm(true);
      } else {
        setError(err.message || '회원 조회에 실패했습니다');
      }
    } finally {
      setLoading(false);
    }
  };

  // 미등록 회원 가입 처리 (가입 시 서버에서 자동 알림톡 발송)
  const handleNewMemberRegister = async () => {
    setShowNewMemberConfirm(false);
    setAutoRegistering(true);
    setError(null);

    const storeId = localStorage.getItem('store_id') || '3';

    try {
      const res = await registerMemberOnly('010' + digits, storeId);
      if (res.success && res.member) {
        setMember(res.member);
        setResult({
          success: true,
          message: '멤버십 가입 완료! 알림톡이 자동 발송되었습니다',
        });
      } else {
        setError(res.message || '회원 가입에 실패했습니다');
      }
    } catch (err) {
      setError(err.message || '회원 가입 중 오류가 발생했습니다');
    } finally {
      setAutoRegistering(false);
    }
  };

  // 알림톡 재발송 (기존 회원용)
  const handleSend = async () => {
    if (!member) return;

    setSending(true);
    setResult(null);

    try {
      const res = await resendQrAlimtalk(member.member_id);
      setResult({ success: true, message: res.message || '알림톡이 발송되었습니다' });
    } catch (err) {
      setResult({ success: false, message: err.message || '발송에 실패했습니다' });
    } finally {
      setSending(false);
    }
  };

  // 초기화
  const handleReset = () => {
    setDigits('');
    setMember(null);
    setResult(null);
    setError(null);
    setShowNewMemberConfirm(false);
  };

  const maskPhone = (p) => {
    if (!p || p.length < 8) return p || '-';
    return p.slice(0, 3) + '-****-' + p.slice(-4);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return dateStr.slice(0, 10);
  };

  return (
    <div className="alimtalk-page">
      {/* 헤더 */}
      <header className="alim-header">
        <button className="btn-back" onClick={() => navigate('/')}>
          ← 메뉴
        </button>
        {/* 헤더를 눌러 전체화면 — **태블릿 앱에서만** (2026-08-06 요구).
            웹(크롬)에서는 제목을 눌렀다고 전체화면이 되면 안 된다. */}
        <div className="alim-header-center" onClick={() => {
          if (!isBridgeApp()) return;
          const el = document.documentElement;
          (el.requestFullscreen || el.webkitRequestFullscreen)?.call(el)?.catch?.(() => {});
        }}>
          <h1>💬 알림톡</h1>
          <StoreBadge />
        </div>
        <div className="alim-header-spacer" />
      </header>

      {/* 미등록 회원 가입 확인 팝업 */}
      {showNewMemberConfirm && (
        <div className="alim-confirm-overlay">
          <div className="alim-confirm-popup">
            <div className="alim-confirm-icon">🆕</div>
            <h3>검색결과가 없습니다</h3>
            <p>멤버십에 가입할까요?</p>
            <div className="alim-confirm-phone">{formatPhone(digits)}</div>
            <p className="alim-confirm-note">가입 시 알림톡이 자동 발송됩니다</p>
            <div className="alim-confirm-buttons">
              <button className="btn-confirm-cancel" onClick={() => setShowNewMemberConfirm(false)}>
                취소
              </button>
              <button className="btn-confirm-ok" onClick={handleNewMemberRegister}>
                가입하기
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 자동 가입 진행 중 오버레이 */}
      {autoRegistering && (
        <div className="alim-confirm-overlay">
          <div className="alim-confirm-popup">
            <div className="alim-spinner" />
            <h3>멤버십 가입 중...</h3>
            <p>잠시만 기다려주세요</p>
          </div>
        </div>
      )}

      <main className="alim-content">
        {/* 전화번호 입력 (숫자패드) */}
        {!member && !loading && (
          <div className="alim-search-section">
            <p className="alim-desc">회원 전화번호를 입력하면<br/>멤버십 가입 알림톡을 발송합니다</p>

            {/* 전화번호 표시 */}
            <div className="alim-phone-display">
              <span className={`alim-phone-text ${digits.length === 0 ? 'placeholder' : ''}`}>
                <span style={{color: '#999'}}>010</span>
                {digits.length > 0 ? `-${digits.slice(0, 4)}` : '-'}
                {digits.length > 4 ? `-${digits.slice(4)}` : ''}
                {digits.length === 0 && <span className="placeholder"> 뒤 8자리 입력</span>}
              </span>
            </div>

            {/* 숫자패드 + 조회 버튼을 하나의 그리드로 */}
            <div className="alim-numpad">
              {NUMPAD_KEYS.map((row, ri) => (
                <div className="alim-numpad-row" key={ri}>
                  {row.map((key) => (
                    <button
                      key={key}
                      className={`alim-numpad-key ${key === 'clear' ? 'key-clear' : ''} ${key === 'backspace' ? 'key-backspace' : ''}`}
                      onClick={() => handleKeyPress(key)}
                    >
                      {key === 'backspace' ? '⌫' : key === 'clear' ? 'C' : key}
                    </button>
                  ))}
                </div>
              ))}
              {/* 조회 버튼을 숫자패드 마지막 행으로 */}
              <button
                className="btn-search"
                onClick={handleSearch}
                disabled={digits.length !== 8}
              >
                조회
              </button>
            </div>

            {error && (
              <div className="alim-error">
                <span>⚠️</span> {error}
              </div>
            )}
          </div>
        )}

        {/* 로딩 */}
        {loading && (
          <div className="alim-loading">
            <div className="alim-spinner" />
            <p>회원 조회 중...</p>
          </div>
        )}

        {/* 회원 정보 + 발송 */}
        {member && !loading && (
          <div className="alim-member-card">
            <div className="alim-member-header">
              <span className="alim-member-avatar">💬</span>
              <div>
                <h2 className="alim-member-name">{member.name || member.nickname || '이름 없음'}</h2>
                <span className="alim-member-id">{member.member_id}</span>
              </div>
            </div>

            <div className="alim-info-grid">
              <div className="alim-info-item">
                <span className="alim-info-label">전화번호</span>
                <span className="alim-info-value">{member.phone_masked || maskPhone(member.phone)}</span>
              </div>
              <div className="alim-info-item">
                <span className="alim-info-label">가입일</span>
                <span className="alim-info-value">{formatDate(member.created_at)}</span>
              </div>
              <div className="alim-info-item">
                <span className="alim-info-label">총 방문</span>
                <span className="alim-info-value">{member.visit_count ?? member.total_visits ?? '-'}회</span>
              </div>
              <div className="alim-info-item">
                <span className="alim-info-label">최근 방문</span>
                <span className="alim-info-value">{formatDate(member.last_visit_at)}</span>
              </div>
            </div>

            {/* 발송 결과 */}
            {result && (
              <div className={`alim-result ${result.success ? 'success' : 'fail'}`}>
                {result.success ? '✅' : '❌'} {result.message}
              </div>
            )}

            {/* 액션 버튼 */}
            <div className="alim-action-buttons">
              {!result?.success && (
                <button
                  className="btn-send"
                  onClick={handleSend}
                  disabled={sending}
                >
                  {sending ? '발송 중...' : '💬 알림톡 발송'}
                </button>
              )}
              <button className="btn-reset" onClick={handleReset}>
                {result?.success ? '다른 회원 발송' : '← 뒤로가기'}
              </button>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default AlimtalkPage;
